import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';

import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { SidebarService } from '../services/sidebar.service';
import { OnboradingService } from '../../onboarding/shared/service/onboarding.service';
import { NotificationSocketService } from '../../../core/shared/services/notification-socket.service';
import { KeycloakService } from 'keycloak-angular';
import { PermissionService } from '../services/permission.service';
import { LanguageService, Language } from '../../../core/shared/services/language.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface NotificationItem {
  ntfctnSeq: number;
  title: string;
  message: string;
  time: string;
  read: boolean;
  pld?: any;
  role?: string;
  notificationType?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslatePipe],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnInit, OnDestroy {
  languages: Language[];
  selectedLanguage: Language;
  constructor(
    public sidebarService: SidebarService,
    private onboardingService: OnboradingService,
    private notificationSocket: NotificationSocketService,
    private keycloak: KeycloakService,
    public permissionService: PermissionService,
    public languageService: LanguageService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {
    this.languages = this.languageService.languages;
    this.selectedLanguage = this.languageService.getCurrentLanguage();
  }

  private socketSub!: Subscription;

  // =====================================
  // USER INFO
  // =====================================

  userName = '';
  userRole = '';
  userInitial = '';

  // =====================================
  // SIDEBAR
  // =====================================

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  // =====================================
  // LANGUAGE
  // =====================================

  isLangOpen = false;

  toggleLang(): void {
    this.isLangOpen = !this.isLangOpen;
    if (this.isLangOpen) this.isNotificationOpen = false;
  }

  selectLanguage(lang: Language): void {
    this.selectedLanguage = lang;
    this.languageService.setLanguage(lang); // ✅ actual switch
    this.isLangOpen = false;
  }

  // =====================================
  // NOTIFICATIONS
  // =====================================

  isNotificationOpen = false;
  notifications: NotificationItem[] = [];
  newCount = 0;

  @ViewChild('notificationWrapper') notificationWrapper?: ElementRef<HTMLElement>;

  // Close the notification dropdown on any press outside its panel.
  // Listens in the CAPTURE phase so it runs before any component's
  // stopPropagation() — a bubbling document:click listener never fires
  // when the click lands on a table/dropdown that stops propagation,
  // which left the popup stuck open.
  private readonly onOutsidePointerDown = (event: PointerEvent): void => {
    if (!this.isNotificationOpen) return;

    const wrapperEl = this.notificationWrapper?.nativeElement;
    const path = event.composedPath();

    if (wrapperEl && !path.includes(wrapperEl)) {
      this.closeNotification();
      // App is zoneless — a native listener doesn't schedule change
      // detection on its own, so the popup would stay painted open.
      this.cdr.markForCheck();
    }
  };

  // Clicks inside an iframe (e.g. document preview) never reach this
  // document; the window losing focus is the only signal we get.
  @HostListener('window:blur')
  onWindowBlur(): void {
    if (this.isNotificationOpen && document.activeElement instanceof HTMLIFrameElement) {
      this.closeNotification();
    }
  }

  // =====================================
  // INIT / DESTROY
  // =====================================

  ngOnInit(): void {
    // ✅ Restore saved language + apply dir/lang on load
    this.selectedLanguage = this.languageService.init();

    // ✅ User info from Keycloak
    this.loadUserInfo();

    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);

    this.getUnreadNotifications();

    this.notificationSocket.connect().then(() => {});

    this.socketSub = this.notificationSocket.notifications$.subscribe((data: any[]) => {
      if (!data.length) return;

      const mapped: NotificationItem[] = data.map((item: any) => ({
        ntfctnSeq: item.notificationSeq,
        title: item.title || this.translate.instant('NOTIFICATIONS.DEFAULT_TITLE'),
        message: item.message || this.translate.instant('NOTIFICATIONS.NO_MESSAGE'),
        time: item.createdDate || this.translate.instant('NOTIFICATIONS.NOW'),
        read: item.readFlag,
        pld: item.pld,
        role: item.role,
        notificationType: item.notificationType,
      }));

      this.notifications = [...mapped, ...this.notifications];
      this.newCount += mapped.length;
      this.showToast(mapped[0]);
    });
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
    clearTimeout(this.toastTimer);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  hasAccess(route: string): boolean {
    return this.permissionService.hasAccess(route);
  }

  // =====================================
  // LOAD USER INFO
  // =====================================

  userAvatarColor = '';

  loadUserInfo(): void {
    const tokenParsed: any = this.keycloak.getKeycloakInstance().tokenParsed;

    if (!tokenParsed) return;

    const firstName = tokenParsed.given_name || '';
    const lastName = tokenParsed.family_name || '';
    this.userName = `${firstName} ${lastName}`.trim() || tokenParsed.preferred_username || 'User';

    const realmRoles: string[] = tokenParsed.realm_access?.roles || [];
    const clientRoles: string[] = tokenParsed.resource_access?.['B2BLoanly']?.roles || [];

    const systemRoles = [
      'offline_access',
      'uma_authorization',
      'default-roles-lonallyb2b',
      'default-roles-lonalynb2b',
    ];

    this.userRole = realmRoles.find((r) => !systemRoles.includes(r)) || clientRoles[0] || 'User';

    this.userInitial = this.userName.charAt(0).toUpperCase();
    this.userAvatarColor = this.generateColor(this.userName);

    localStorage.setItem('user_name', this.userName);
    localStorage.setItem('user_role', this.userRole);
  }

  // ✅ Username se consistent color generate karo
  generateColor(name: string): string {
    const colors = [
      '#6366f1',
      '#8b5cf6',
      '#ec4899',
      '#f59e0b',
      '#10b981',
      '#3b82f6',
      '#ef4444',
      '#14b8a6',
      '#f97316',
      '#06b6d4',
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  // =====================================
  // TOAST
  // =====================================

  private toastTimer: any;

  dismissToast(): void {
    clearTimeout(this.toastTimer);
  }

  showToast(item: NotificationItem): void {
    this.notificationSocket.showToast(item.title, item.message);
    this.playSound();
  }

  // =====================================
  // SOUND
  // =====================================

  private audioCtx: AudioContext | null = null;

  private unlockAudio(): void {
    if (!this.audioCtx) {
      this.audioCtx = new AudioContext();
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
  }

  playSound(): void {
    try {
      if (!this.audioCtx) return;
      const ctx = this.audioCtx;

      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.connect(g1);
      g1.connect(ctx.destination);
      o1.type = 'sine';
      o1.frequency.setValueAtTime(880, ctx.currentTime);
      g1.gain.setValueAtTime(0.25, ctx.currentTime);
      g1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      o1.start(ctx.currentTime);
      o1.stop(ctx.currentTime + 0.25);

      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.connect(g2);
      g2.connect(ctx.destination);
      o2.type = 'sine';
      o2.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
      g2.gain.setValueAtTime(0.2, ctx.currentTime + 0.15);
      g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      o2.start(ctx.currentTime + 0.15);
      o2.stop(ctx.currentTime + 0.45);
    } catch (e) {}
  }

  // =====================================
  // NOTIFICATION PANEL
  // =====================================

  toggleNotification(): void {
    this.unlockAudio();
    this.isNotificationOpen = !this.isNotificationOpen;
    if (this.isNotificationOpen) {
      this.isLangOpen = false;
      this.newCount = 0;
      this.getUnreadNotifications();
    }
  }

  closeNotification(): void {
    this.isNotificationOpen = false;
  }

  getUnreadNotifications(): void {
    this.onboardingService.getUnreadNotifications().subscribe({
      next: (res: any) => {
        if (res?.__hasError) {
          console.error(res.error);
          this.notifications = [];
          return;
        }

        if (!Array.isArray(res)) {
          console.error('Expected array but got:', res);
          this.notifications = [];
          return;
        }

        this.notifications = res.map((item: any) => ({
          ntfctnSeq: item.notificationSeq,
          title: item.title || this.translate.instant('NOTIFICATIONS.DEFAULT_TITLE'),
          message: item.message || this.translate.instant('NOTIFICATIONS.NO_MESSAGE'),
          time: item.createdDate || this.translate.instant('NOTIFICATIONS.NOW'),
          read: item.readFlag,
          pld: item.pld,
          role: item.role,
          notificationType: item.notificationType,
        }));
      },
    });
  }

  openNotification(item: NotificationItem): void {
    if (!item.read) {
      item.read = true;

      // Drop it from the badge/list count immediately, don't wait for the API.
      this.notifications = this.notifications.filter((x) => x.ntfctnSeq !== item.ntfctnSeq);

      this.onboardingService
        .markNotificationRead({
          ntfctnSeq: item.ntfctnSeq,
          rdFlg: true,
          dltFlg: false,
        })
        .subscribe();
    }
    this.closeNotification();
    this.router.navigate(['/notifications'], {
      queryParams: { notificationSeq: item.ntfctnSeq },
    });
  }

  markAsRead(item: NotificationItem): void {
    if (item.read) return;
    this.onboardingService
      .markNotificationRead({
        ntfctnSeq: item.ntfctnSeq,
        rdFlg: true,
        dltFlg: false,
      })
      .subscribe({
        next: () => {
          item.read = true;
          this.notifications = this.notifications.filter((x) => x.ntfctnSeq !== item.ntfctnSeq);
        },
      });
  }

  markAllAsRead(): void {
    this.notifications
      .filter((x) => !x.read)
      .forEach((item) => {
        this.onboardingService
          .markNotificationRead({
            ntfctnSeq: item.ntfctnSeq,
            rdFlg: true,
            dltFlg: false,
          })
          .subscribe({
            next: () => {
              item.read = true;
            },
          });
      });
    this.notifications = [];
    this.newCount = 0;
  }
}
